<script setup lang="ts">
import _ from 'lodash'
import { arrayify, ordinal } from '../util'
import { statnames, totalLevel, type BaseStats, type Spellcasting } from './statblock'
import { joinDice, resolveDice } from '~/assets/dice/diceEval'

const progression: Record<number, number[]> = {
  1: [0, 2],
  2: [0, 3],
  3: [0, 4, 2],
  4: [0, 4, 3],
  5: [0, 4, 3, 2],
  6: [0, 4, 3, 3],
  7: [0, 4, 3, 3, 1],
  8: [0, 4, 3, 3, 2],
  9: [0, 4, 3, 3, 3, 1],
  10: [0, 4, 3, 3, 3, 2],
  11: [0, 4, 3, 3, 3, 2, 1],
  12: [0, 4, 3, 3, 3, 2, 1],
  13: [0, 4, 3, 3, 3, 2, 1, 1],
  14: [0, 4, 3, 3, 3, 2, 1, 1],
  15: [0, 4, 3, 3, 3, 2, 1, 1, 1],
  16: [0, 4, 3, 3, 3, 2, 1, 1, 1],
  17: [0, 4, 3, 3, 3, 2, 1, 1, 1, 1],
  18: [0, 4, 3, 3, 3, 3, 1, 1, 1, 1],
  19: [0, 4, 3, 3, 3, 3, 2, 1, 1, 1],
  20: [0, 4, 3, 3, 3, 3, 2, 2, 1, 1],
}

const progressionWarlock: Record<number, number[]> = {
  1: [0, 1],
  2: [0, 2],
  3: [0, 0, 2],
  4: [0, 0, 2],
  5: [0, 0, 0, 2],
  6: [0, 0, 0, 2],
  7: [0, 0, 0, 0, 2],
  8: [0, 0, 0, 0, 2],
  9: [0, 0, 0, 0, 0, 2],
  10: [0, 0, 0, 0, 0, 2],
  11: [0, 0, 0, 0, 0, 3, 1],
  12: [0, 0, 0, 0, 0, 3, 1],
  13: [0, 0, 0, 0, 0, 3, 1, 1],
  14: [0, 0, 0, 0, 0, 3, 1, 1],
  15: [0, 0, 0, 0, 0, 3, 1, 1, 1],
  16: [0, 0, 0, 0, 0, 3, 1, 1, 1],
  17: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  18: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  19: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
  20: [0, 0, 0, 0, 0, 4, 1, 1, 1, 1],
}

const props = defineProps<{
  stats: BaseStats
  data: Spellcasting
  player?: boolean
}>()

const name = computed(() => (props.player ? 'you' : props.stats.generalName || props.stats.title))

const capitalizedName = computed(() => _.capitalize(name.value))

const stat = computed(() => props.data.stat)
const statName = computed(() => statnames[stat.value])

const spellAttackModifier = computed(() => {
  const bonus = props.data.bonusAttacks ?? ''
  return joinDice(stat.value, 'prof', bonus)
})

const spellSaveDC = computed(() => {
  const bonus = props.data.bonusSaves ?? ''
  return joinDice(8, stat.value, 'prof', bonus, 'exhdc')
})

const mods = useMods()
const level = computed(() => resolveLevel(props.data))

const resolveLevel = (data: Spellcasting) => {
  if (data.level) {
    return resolveDice(data.level, mods(), true)
  }
  return totalLevel(props.stats.level)
}

const casterLevel = computed(() => {
  const resolve = (data: Spellcasting) => {
    const level = resolveLevel(data)
    if (data.progression === 'half') {
      return Math.ceil(level / 2)
    } else if (data.progression === 'third') {
      return Math.floor(level / 3)
    } else {
      return level
    }
  }
  return _.sum(
    arrayify(props.stats.spellcasting!)
      .filter((data) => data.progression !== 'innate')
      .map(resolve),
  )
})
const spellSlots = computed(() => {
  if (props.data.progression === 'warlock') {
    return progressionWarlock[casterLevel.value]
  }
  return progression[casterLevel.value]
})
const leveledSpells = computed(() => {
  const spells = spellSlots.value
    .map((slots, level) => ({
      level,
      slots: slots + (props.data.bonusSlots?.[level + 1] ?? 0),
      spells: formatSpells('' + level),
    }))
    .filter((lvl) => lvl.slots > 0 || lvl.spells.length > 0)

  return spells
})

// todo: sort levels (0 > 3 > 1 etc)
const innateSpells = computed(() => {
  const spells = Object.keys(props.data.spells)
    .map((uses: string) => ({
      uses,
      spells: formatSpells(uses),
    }))
    .filter(({ spells }) => spells.length)

  return spells
})

const formatSpells = (level: string) => {
  const spells = props.data.spells?.[level] || []
  const res = spells.map((spell: string) => {
    const noteRx = / *\(([^)]*)\)/
    const isEstiaRx = /^E,?/
    const [, note = ''] = noteRx.exec(spell) ?? []
    const isDDB = !note.match(isEstiaRx)
    const name = spell.replace(`(${note})`, '').trim()
    return {
      spell: name,
      wiki: isDDB ? `ddb spell ${name}` : name,
      note: note.replace(isEstiaRx, '').trim(),
    }
  })
  return res
}

const onShortRest = (level: number) => {
  if (props.data.progression === 'warlock' && level <= 5) {
    return 'reset'
  }
  return ''
}
</script>

<template>
  <template v-if="data.progression !== 'innate'">
    <p>
      <strong>Spellcasting</strong>: {{ capitalizedName }} {{ player ? 'are' : 'is' }} a {{ ordinal(level) }}-level
      spellcaster. {{ player ? 'Your' : 'Their' }} spellcasting ability is {{ statName }} (spell save DC
      <Dice value :dice="spellSaveDC" />, <Dice :dice="spellAttackModifier" /> to hit).
    </p>
    <ul class="spellist">
      <li v-for="spellLevel in leveledSpells" :key="spellLevel.level">
        {{ spellLevel.level === 0 ? 'Cantrips' : ordinal(spellLevel.level) + ' level '
        }}<Counter
          v-if="spellLevel.slots > 0"
          :uses="spellLevel.slots"
          parens
          :name="`ss${spellLevel.level}`"
          :sr="onShortRest(spellLevel.level)"
        />:
        <span v-for="spell in spellLevel.spells" :key="spell.spell" class="comma-sep"
          ><WikiLink :wiki="spell.wiki" :label-override="spell.spell" /><span v-if="spell.note">
            ({{ spell.note }})</span
          ></span
        >
      </li>
    </ul>
  </template>
  <template v-else>
    <p>
      <strong>Innate Spellcasting</strong>:
      {{ capitalizedName === 'You' ? 'Your' : `${capitalizedName}'s` }} spellcasting ability is {{ statName }} (spell
      save DC <Dice value :dice="spellSaveDC" />, <Dice :dice="spellAttackModifier" /> to hit).
    </p>
    <ul class="spellist">
      <li v-for="spellLevel in innateSpells" :key="spellLevel.uses">
        {{ spellLevel.uses == '0' ? 'At will:' : `${spellLevel.uses}/lr each:` }}
        <span v-for="spell in spellLevel.spells" :key="spell.spell" class="comma-sep"
          ><WikiLink :wiki="spell.wiki" :label-override="spell.spell" /><span v-if="spell.note">
            ({{ spell.note }})</span
          ><Counter
            v-if="player && spellLevel.uses !== '0'"
            :uses="spellLevel.uses"
            :name="`innate-${spell}`"
            lr="reset"
            hide-reset
        /></span>
      </li>
    </ul>
    {{ data.extra ? data.extra : '' }}
  </template>
</template>

<style scoped lang="postcss">
.spellcasting {
  display: contents;
}
</style>
